import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Check, CloudUpload, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { createEmployee, updateEmployee } from "../../services/api";
import { resizeImage } from "../directory/directoryUtils";

export const CONTRACT_LABELS = { cdi: "CDI", cdd: "CDD", alternance: "Alternance", stage: "Stage", freelance: "Freelance", autre: "Autre" };

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", address: "", postal_code: "", city: "",
  birth_date: "", social_security_number: "", position: "", contract_type: "cdi",
  hire_date: "", end_date: "", salary_gross_monthly: "", iban: "", notes: "", is_active: true,
  photo: null,
};

const buildPayload = (form) => {
  const payload = {
    ...form,
    salary_gross_monthly: form.salary_gross_monthly !== "" ? parseFloat(form.salary_gross_monthly) : null,
  };
  ["email", "phone", "address", "postal_code", "city", "birth_date", "social_security_number", "position", "hire_date", "end_date", "iban", "notes"].forEach((k) => {
    if (payload[k] === "") payload[k] = null;
  });
  return payload;
};

export const EmployeeFormDialog = ({ open, onClose, employee, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");
  const formRef = useRef(EMPTY);
  const draftRef = useRef(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const savedRef = useRef(false);
  const photoRef = useRef(null);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file, 400);
      set("photo")(dataUrl);
    } catch {
      toast.error("Impossible de lire l'image");
    }
  };

  useEffect(() => {
    if (open) {
      const init = employee ? { ...EMPTY, ...Object.fromEntries(Object.entries(employee).map(([k, v]) => [k, v ?? ""])), is_active: employee.is_active !== false } : EMPTY;
      setForm(init);
      formRef.current = init;
      draftRef.current = null;
      savedRef.current = false;
      setAutoStatus("");
    }
    return () => clearTimeout(timerRef.current);
  }, [open, employee]);

  const targetId = () => employee?.id || draftRef.current;

  const autoSave = async () => {
    const f = formRef.current;
    if (!f.first_name.trim() || !f.last_name.trim()) return;
    if (busyRef.current) {
      timerRef.current = setTimeout(autoSave, 500);
      return;
    }
    busyRef.current = true;
    setAutoStatus("saving");
    try {
      const id = targetId();
      if (id) {
        const res = await updateEmployee(id, buildPayload(f));
        if (res.data.user_account?.action === "created") {
          toast.success(`Compte utilisateur créé (${res.data.user_account.email})`);
        }
      } else {
        const res = await createEmployee(buildPayload(f));
        draftRef.current = res.data.id;
        if (res.data.user_account?.action === "created") {
          toast.success(`Compte utilisateur créé pour ${f.first_name} (${res.data.user_account.email})`);
        }
      }
      savedRef.current = true;
      setAutoStatus("saved");
    } catch {
      setAutoStatus("error");
    } finally {
      busyRef.current = false;
    }
  };

  const scheduleAutoSave = () => {
    clearTimeout(timerRef.current);
    setAutoStatus((s) => (s === "saving" ? s : ""));
    timerRef.current = setTimeout(autoSave, 900);
  };

  const set = (k) => (e) => {
    const next = { ...formRef.current, [k]: e?.target ? e.target.value : e };
    formRef.current = next;
    setForm(next);
    scheduleAutoSave();
  };

  const handleClose = async () => {
    clearTimeout(timerRef.current);
    const f = formRef.current;
    if (f.first_name.trim() && f.last_name.trim() && autoStatus !== "saved") {
      await autoSave();
    }
    if (savedRef.current) onSaved();
    onClose();
  };

  const handleSave = async () => {
    clearTimeout(timerRef.current);
    const f = formRef.current;
    if (!f.first_name.trim() || !f.last_name.trim()) { toast.error("Prénom et nom requis"); return; }
    try {
      setSaving(true);
      const id = targetId();
      const res = id
        ? await updateEmployee(id, buildPayload(f))
        : await createEmployee(buildPayload(f));
      if (res.data.user_account?.action === "created") {
        toast.success(`Compte utilisateur créé (${res.data.user_account.email})`);
      }
      toast.success(employee || id ? "Employé mis à jour" : "Employé créé");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{employee ? "Modifier l'employé" : "Nouvel employé"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Informations personnelles</h4>
            <div className="flex gap-4">
              <div className="shrink-0">
                <button
                  className="w-16 h-16 rounded-full bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden hover:border-[#26788A] transition-colors"
                  onClick={() => photoRef.current?.click()}
                  title="Photo de profil"
                  data-testid="emp-photo-btn">
                  {form.photo ? (
                    <img src={form.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-slate-400">
                      {((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "+"}
                    </span>
                  )}
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="emp-photo-input" />
                {form.photo && (
                  <button className="block mx-auto mt-1 text-[9px] text-red-400 hover:text-red-500" onClick={() => set("photo")(null)} data-testid="emp-photo-remove">Retirer</button>
                )}
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Prénom *</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="emp-firstname-input" /></div>
              <div><Label className="mb-1 block text-xs">Nom *</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="emp-lastname-input" /></div>
              <div><Label className="mb-1 block text-xs">Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="emp-email-input" /></div>
              <div><Label className="mb-1 block text-xs">Téléphone</Label><Input value={form.phone} onChange={set("phone")} /></div>
              <div className="col-span-2"><Label className="mb-1 block text-xs">Adresse</Label><Input value={form.address} onChange={set("address")} /></div>
              <div><Label className="mb-1 block text-xs">Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")} /></div>
              <div><Label className="mb-1 block text-xs">Ville</Label><Input value={form.city} onChange={set("city")} /></div>
              <div><Label className="mb-1 block text-xs">Date de naissance</Label><Input type="date" value={form.birth_date} onChange={set("birth_date")} /></div>
              <div><Label className="mb-1 block text-xs">N° sécurité sociale</Label><Input value={form.social_security_number} onChange={set("social_security_number")} /></div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Informations professionnelles</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Poste</Label><Input value={form.position} onChange={set("position")} placeholder="Ex : Community Manager" data-testid="emp-position-input" /></div>
              <div>
                <Label className="mb-1 block text-xs">Type de contrat</Label>
                <Select value={form.contract_type} onValueChange={set("contract_type")}>
                  <SelectTrigger data-testid="emp-contract-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTRACT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1 block text-xs">Date d'embauche</Label><Input type="date" value={form.hire_date} onChange={set("hire_date")} data-testid="emp-hiredate-input" /></div>
              <div><Label className="mb-1 block text-xs">Date de fin (si CDD/stage)</Label><Input type="date" value={form.end_date} onChange={set("end_date")} /></div>
              <div><Label className="mb-1 block text-xs">Salaire brut mensuel (€)</Label><Input type="number" value={form.salary_gross_monthly} onChange={set("salary_gross_monthly")} /></div>
              <div><Label className="mb-1 block text-xs">IBAN</Label><Input value={form.iban} onChange={set("iban")} /></div>
            </div>
          </div>
          <div><Label className="mb-1 block text-xs">Notes internes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={set("is_active")} data-testid="emp-active-switch" />
            <span className="text-sm text-slate-700">Employé actif</span>
          </div>
        </div>
        <DialogFooter className="items-center gap-3">
          <span className="text-xs mr-auto flex items-center gap-1.5" data-testid="autosave-status">
            {autoStatus === "saving" && <><CloudUpload size={13} className="text-[#26788A] animate-pulse" /><span className="text-slate-400">Enregistrement automatique…</span></>}
            {autoStatus === "saved" && <><Check size={13} className="text-emerald-500" /><span className="text-emerald-600">Enregistré automatiquement</span></>}
            {autoStatus === "error" && <><AlertTriangle size={13} className="text-red-500" /><span className="text-red-500">Erreur d'enregistrement auto</span></>}
          </span>
          <Button variant="outline" onClick={handleClose} data-testid="close-employee-btn">Fermer</Button>
          <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={handleSave} disabled={saving} data-testid="save-employee-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            {employee ? "Enregistrer" : "Créer l'employé"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
